// ─── riskProfileFlags.js ────────────────────────────────────────────────
// V97.58 (audit P4.7, volet TCA — 2026-07-02) — flags manuels « profil à
// risque », posés par la PRATICIENNE dans la fiche cliente (jamais par la
// cliente, jamais par détection automatique : « suspecté » est un jugement
// clinique, non détectable de façon fiable — réf.
// docs/VALIDATION-CLINIQUE-ANISSA-V1.md, mapping profils #4/#5/#6/#13).
//
// Rôle du module : REGISTRE des flags (ids + libellés FR d'UI/clairance) +
// résolveur pur. Les CONTRAINTES de prompt associées vivent par langue, à côté
// des blocs maternels : RISK_PROFILE_SAFETY_FR (_clinicalContext.fr.js) et
// RISK_PROFILE_SAFETY_EN (en.js) — même architecture que MATERNAL_SAFETY_*.
//
// Comportement système (décision doc validation, pas de ce module) :
//   1. contraintes injectées dans le bloc sécurité TOUJOURS ACTIF (jamais OPT-IN) ;
//   2. « validation obligatoire » : violation HIGH dans la clairance des 4 portes
//      (assertPlanClinicallyCleared) → aucun plan ne sort sans override conscient.
//
// Aucun import — consommable des deux côtés (FR/EN/clairance/UI) sans risque
// de cycle. Pure data + fonctions pures, fail-safe.

// `mandatoryValidation` : true = aucun plan ne sort sans validation consciente
// (violation HIGH à la clairance) ; false = contraintes injectées dans les
// prompts, mais AUCUN blocage (comportement B « flag + contraintes »).
// Classification par flag = DÉCISION ANISSA, scellée 2026-07-02 (réponses au
// doc VALIDATION-CLINIQUE-ANISSA-V2-SESSION-JUILLET-2026.md, point 2) :
// chirurgie récente ne bloque pas — « toutes les chirurgies récentes ne
// présentent pas le même niveau de risque, je garde mon jugement clinique ».
export const RISK_PROFILE_FLAGS = Object.freeze([
  Object.freeze({ id: 'tca', labelFr: 'TCA suspecté ou déclaré (trouble du comportement alimentaire)', mandatoryValidation: true }),
  Object.freeze({ id: 'cancer_actif', labelFr: 'Cancer en traitement actif', mandatoryValidation: true }),
  Object.freeze({ id: 'insuffisance_hepatique', labelFr: 'Insuffisance hépatique', mandatoryValidation: true }),
  Object.freeze({ id: 'chirurgie_recente', labelFr: 'Chirurgie récente (moins de 3 mois)', mandatoryValidation: false }),
]);

const VALID_IDS = new Set(RISK_PROFILE_FLAGS.map((f) => f.id));
const LABELS_FR = new Map(RISK_PROFILE_FLAGS.map((f) => [f.id, f.labelFr]));

export function hasRiskProfileAssessment(form) {
  return Boolean(
    form
    && typeof form === 'object'
    && form.profilsRisqueVerifiesParAnissa === true
    && Array.isArray(form.flagsProfilRisque),
  );
}

// Oui/Non quelle que soit la casse : « oui » saisi à la main ne doit pas
// arrêter une consultation.
const PHASE_TRANSITION_MATERNAL_VALUES = new Set(['oui', 'non']);
function reponseMaternelle(valeur) {
  return typeof valeur === 'string'
    && PHASE_TRANSITION_MATERNAL_VALUES.has(valeur.trim().toLowerCase());
}

export function collectPhaseTransitionSafetyIssues(form) {
  if (!form || typeof form !== 'object' || Array.isArray(form)) {
    return Object.freeze([{ field: 'phaseSafety', message: 'Le contexte de sécurité structuré est indisponible.' }]);
  }
  const issues = [];
  for (const field of ['grossesseActuelle', 'allaitement', 'postPartum']) {
    if (!reponseMaternelle(form[field])) {
      issues.push({ field, severity: 'blocking', message: `Le champ ${field} doit être confirmé par Oui ou Non avant la transition.` });
    }
  }
  if (form.profilsRisqueVerifiesParAnissa !== true || !Array.isArray(form.flagsProfilRisque)) {
    issues.push({
      field: 'profilsRisqueVerifiesParAnissa',
      severity: 'to_confirm',
      message: 'les profils à risque n’ont pas encore été marqués comme vérifiés.',
    });
  } else {
    const unknownFlags = findUnknownRiskFlags(form);
    if (unknownFlags.length > 0) {
      // Un marqueur de risque que le code ne sait pas interpréter reste
      // bloquant : ce n'est pas une case oubliée, c'est un risque clinique
      // que personne ne verrait passer.
      issues.push({
        field: 'flagsProfilRisque',
        severity: 'blocking',
        message: `Profil à risque inconnu : ${unknownFlags.join(', ')}.`,
      });
    }
  }
  return Object.freeze(issues.map((issue) => Object.freeze(issue)));
}

/**
 * Construit la preuve structurée et strictement limitée qu'Anissa confirme
 * avant de préparer une transition de phase. Ce helper ne prend aucune
 * décision clinique : il refuse simplement les champs absents, ambigus ou
 * inconnus afin que la base puisse comparer le même snapshot exact.
 *
 * @param {object|null|undefined} form
 * @returns {{
 *   grossesseActuelle: 'Oui'|'Non',
 *   allaitement: 'Oui'|'Non',
 *   postPartum: 'Oui'|'Non',
 *   profilsRisqueVerifiesParAnissa: true,
 *   flagsProfilRisque: string[],
 * }}
 */
export function buildPhaseTransitionSafetySnapshot(form) {
  // Seules les réponses maternelles ferment le flux : sans Oui ou Non, la
  // sécurité maternelle ne peut pas être arbitrée. L'attestation de relecture
  // des profils à risque, elle, est signalée ailleurs sans jamais bloquer.
  const bloquantes = collectPhaseTransitionSafetyIssues(form)
    .filter((issue) => issue.severity !== 'to_confirm');
  if (bloquantes.length > 0) throw new Error(bloquantes[0].message);
  return Object.freeze({
    grossesseActuelle: form.grossesseActuelle,
    allaitement: form.allaitement,
    postPartum: form.postPartum,
    // Le snapshot n'affirme jamais une relecture qui n'a pas eu lieu.
    profilsRisqueVerifiesParAnissa: form.profilsRisqueVerifiesParAnissa === true,
    flagsProfilRisque: Object.freeze(
      Array.isArray(form.flagsProfilRisque) ? resolveRiskProfileFlags(form) : [],
    ),
  });
}

/**
 * Résout les flags « profil à risque » posés sur la fiche (form.flagsProfilRisque).
 * Fail-safe : champ absent / non-tableau / ids inconnus → ignorés sans throw.
 * Dédoublonne et préserve l'ordre du registre (stable pour l'affichage et les prompts).
 *
 * @param {object|null|undefined} form
 * @returns {string[]} ids valides, ordonnés comme RISK_PROFILE_FLAGS
 */
export function resolveRiskProfileFlags(form) {
  if (!form || typeof form !== 'object') return [];
  const raw = form.flagsProfilRisque;
  if (!Array.isArray(raw)) return [];
  const present = new Set(raw.map((x) => String(x)).filter((id) => VALID_IDS.has(id)));
  return RISK_PROFILE_FLAGS.filter((f) => present.has(f.id)).map((f) => f.id);
}

/**
 * Sous-ensemble des flags résolus qui imposent la VALIDATION OBLIGATOIRE
 * (mandatoryValidation: true). C'est CETTE liste que consomme la clairance —
 * les contraintes de prompt, elles, s'appliquent à TOUS les flags résolus.
 *
 * @param {object|null|undefined} form
 * @returns {string[]} ids bloquants, ordonnés comme RISK_PROFILE_FLAGS
 */
export function resolveMandatoryRiskFlags(form) {
  const resolved = new Set(resolveRiskProfileFlags(form));
  return RISK_PROFILE_FLAGS
    .filter((f) => f.mandatoryValidation && resolved.has(f.id))
    .map((f) => f.id);
}

/**
 * Ids présents dans form.flagsProfilRisque mais ABSENTS du registre (donnée
 * corrompue, id renommé, faute de frappe en DB). Côté PROMPT ils ne produisent
 * aucune contrainte (on ne peut pas inventer un texte) — c'est la CLAIRANCE
 * qui ferme : tout id inconnu = violation HIGH (fail-closed : une donnée de
 * sécurité posée par la praticienne mais illisible ne doit JAMAIS disparaître
 * en silence avec un feu vert).
 *
 * @param {object|null|undefined} form
 * @returns {string[]} ids inconnus, dédoublonnés
 */
export function findUnknownRiskFlags(form) {
  if (!form || typeof form !== 'object') return [];
  const raw = form.flagsProfilRisque;
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((x) => String(x)).filter((id) => !VALID_IDS.has(id)))];
}

/**
 * Libellé FR d'un flag (UI fiche + messages de clairance).
 * @param {string} id
 * @returns {string} libellé, ou l'id brut si inconnu (jamais vide)
 */
export function riskFlagLabelFr(id) {
  return LABELS_FR.get(id) || String(id);
}

function strictRiskPolicies(manifest) {
  if (!manifest || manifest.ready !== true || manifest.registryKey !== 'mgd_plan_generation'
      || !Array.isArray(manifest.riskProfiles) || manifest.riskProfiles.length === 0) {
    throw new Error('clinical_policy_not_ready');
  }
  const rows = manifest.riskProfiles;
  if (rows.some((row) => !row || row.enabled !== true
      || row.decision_status !== 'validated_anissa'
      || typeof row.flag_id !== 'string' || !row.flag_id
      || typeof row.label_fr !== 'string' || !row.label_fr.trim()
      || typeof row.mandatory_validation !== 'boolean')) {
    throw new Error('risk_profile_policy_incomplete');
  }
  return rows;
}

/**
 * Résolveur réservé au chemin MGD. Contrairement au résolveur historique, il
 * ne consulte jamais RISK_PROFILE_FLAGS : les ids autorisés proviennent
 * exclusivement du snapshot PostgreSQL scellé.
 */
export function resolveMgdRiskProfileFlags(form, manifest) {
  const rows = strictRiskPolicies(manifest);
  const raw = form?.flagsProfilRisque;
  if (!Array.isArray(raw)) throw new Error('risk_profile_assessment_missing');
  const present = new Set(raw.map((value) => String(value)));
  return rows.filter((row) => present.has(row.flag_id)).map((row) => row.flag_id);
}

export function findUnknownMgdRiskFlags(form, manifest) {
  const known = new Set(strictRiskPolicies(manifest).map((row) => row.flag_id));
  const raw = form?.flagsProfilRisque;
  if (!Array.isArray(raw)) throw new Error('risk_profile_assessment_missing');
  return [...new Set(raw.map((value) => String(value)).filter((id) => !known.has(id)))];
}

export function resolveMgdMandatoryRiskFlags(form, manifest) {
  const rows = strictRiskPolicies(manifest);
  const resolved = new Set(resolveMgdRiskProfileFlags(form, manifest));
  return rows.filter((row) => row.mandatory_validation && resolved.has(row.flag_id))
    .map((row) => row.flag_id);
}

export function mgdRiskFlagLabelFr(id, manifest) {
  const row = strictRiskPolicies(manifest).find((candidate) => candidate.flag_id === id);
  if (!row) throw new Error('risk_profile_policy_incomplete');
  return row.label_fr;
}
